import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  BlockOutlined as BlockIcon,
} from '@mui/icons-material';

const RevokeAccessDialog = ({
  open,
  onClose,
  onConfirm,
  principalType,
  principalName,
  assignments = [],
  applicationAssignments = [],
  isRevoking,
}) => {
  const [confirmInput, setConfirmInput] = useState('');

  const handleClose = () => {
    setConfirmInput('');
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    setConfirmInput('');
  };

  const isUser = principalType === 'user';

  const directAssignments = isUser
    ? assignments.filter((a) => a.assignmentType === 'direct')
    : assignments;
  const indirectAssignments = isUser
    ? assignments.filter((a) => a.assignmentType !== 'direct')
    : [];

  const directAppAssignments = isUser
    ? applicationAssignments.filter((a) => a.assignmentType === 'direct')
    : applicationAssignments;
  const indirectAppAssignments = isUser
    ? applicationAssignments.filter((a) => a.assignmentType !== 'direct')
    : [];

  const totalRevocable = directAssignments.length + directAppAssignments.length;
  const hasRevocableAccess = totalRevocable > 0;
  const isConfirmEnabled = confirmInput === 'revoke' && hasRevocableAccess && !isRevoking;

  const tableCellSx = {
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 500,
    color: '#253E66',
    fontSize: '0.85rem',
  };

  const headerCellSx = {
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    color: '#253E66',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '2px solid #E5E5E5',
  };

  const dimmedRowSx = {
    opacity: 0.5,
    '&:last-child td': { borderBottom: 0 },
  };

  const normalRowSx = {
    '&:last-child td': { borderBottom: 0 },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#E74C3C',
          color: '#FFFFFF',
          fontFamily: 'Bitter, Georgia, serif',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pr: 6,
        }}
      >
        <WarningIcon />
        Revoke All Access
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            mt: 1,
            backgroundColor: 'rgba(231, 76, 60, 0.08)',
            border: '1px solid rgba(231, 76, 60, 0.2)',
            color: '#253E66',
            fontFamily: 'Metropolis, sans-serif',
            '& .MuiAlert-icon': { color: '#E74C3C' },
          }}
        >
          This will permanently revoke all direct access for <strong>{principalName}</strong>. This action cannot be undone.
        </Alert>

        {!hasRevocableAccess && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(70, 152, 211, 0.1)',
              border: '1px solid rgba(70, 152, 211, 0.2)',
              color: '#253E66',
              fontFamily: 'Metropolis, sans-serif',
              '& .MuiAlert-icon': { color: '#4698D3' },
            }}
          >
            No direct access to revoke for this {principalType}.
          </Alert>
        )}

        {/* Permission Set Assignments */}
        {(directAssignments.length > 0 || indirectAssignments.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
                mb: 1,
              }}
            >
              Permission Set Assignments ({directAssignments.length} to revoke{indirectAssignments.length > 0 ? `, ${indirectAssignments.length} inherited` : ''})
            </Typography>
            <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Permission Set', 'Account', ...(isUser ? ['Access Type'] : [])].map((header) => (
                      <TableCell key={header} sx={headerCellSx}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {directAssignments.map((assignment) => (
                    <TableRow key={assignment.id} sx={normalRowSx}>
                      <TableCell sx={tableCellSx}>{assignment.permissionSetName}</TableCell>
                      <TableCell sx={tableCellSx}>
                        {assignment.accountName || assignment.accountId}
                      </TableCell>
                      {isUser && (
                        <TableCell>
                          <Chip
                            label="Direct"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(231, 76, 60, 0.1)',
                              border: '1px solid rgba(231, 76, 60, 0.2)',
                              color: '#E74C3C',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {indirectAssignments.map((assignment) => (
                    <TableRow key={assignment.id} sx={dimmedRowSx}>
                      <TableCell sx={tableCellSx}>{assignment.permissionSetName}</TableCell>
                      <TableCell sx={tableCellSx}>
                        {assignment.accountName || assignment.accountId}
                      </TableCell>
                      {isUser && (
                        <TableCell>
                          <Chip
                            label={`via ${assignment.groupName}`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(214, 71, 148, 0.1)',
                              border: '1px solid rgba(214, 71, 148, 0.2)',
                              color: '#D64794',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {indirectAssignments.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  mt: 0.5,
                  display: 'block',
                }}
              >
                Dimmed rows are inherited from groups and will not be affected.
              </Typography>
            )}
          </Box>
        )}

        {/* Application Assignments */}
        {(directAppAssignments.length > 0 || indirectAppAssignments.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
                mb: 1,
              }}
            >
              Application Assignments ({directAppAssignments.length} to revoke{indirectAppAssignments.length > 0 ? `, ${indirectAppAssignments.length} inherited` : ''})
            </Typography>
            <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Application Name', ...(isUser ? ['Access Type'] : [])].map((header) => (
                      <TableCell key={header} sx={headerCellSx}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {directAppAssignments.map((appAssignment) => (
                    <TableRow key={appAssignment.applicationId} sx={normalRowSx}>
                      <TableCell sx={tableCellSx}>
                        {appAssignment.applicationName || appAssignment.clientId}
                      </TableCell>
                      {isUser && (
                        <TableCell>
                          <Chip
                            label="Direct"
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(231, 76, 60, 0.1)',
                              border: '1px solid rgba(231, 76, 60, 0.2)',
                              color: '#E74C3C',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {indirectAppAssignments.map((appAssignment) => (
                    <TableRow key={appAssignment.applicationId} sx={dimmedRowSx}>
                      <TableCell sx={tableCellSx}>
                        {appAssignment.applicationName || appAssignment.clientId}
                      </TableCell>
                      {isUser && (
                        <TableCell>
                          <Chip
                            label={`via ${appAssignment.groupName}`}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(214, 71, 148, 0.1)',
                              border: '1px solid rgba(214, 71, 148, 0.2)',
                              color: '#D64794',
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {indirectAppAssignments.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  mt: 0.5,
                  display: 'block',
                }}
              >
                Dimmed rows are inherited from groups and will not be affected.
              </Typography>
            )}
          </Box>
        )}

        {/* Confirmation Input */}
        {hasRevocableAccess && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
                fontWeight: 500,
                mb: 1,
              }}
            >
              To confirm, type <strong>"revoke"</strong>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              disabled={isRevoking}
              placeholder='Type "revoke" to confirm'
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  '& fieldset': {
                    borderColor: '#E5E5E5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#E74C3C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E74C3C',
                  },
                },
              }}
            />
          </Box>
        )}
        </motion.div>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isRevoking}
          variant="outlined"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            color: '#666666',
            borderColor: '#E5E5E5',
            borderRadius: '6px',
            '&:hover': {
              borderColor: '#BDBDBD',
              backgroundColor: '#F9FBFD',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          variant="contained"
          startIcon={isRevoking ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
          sx={{
            backgroundColor: '#E74C3C',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#C0392B',
            },
            '&:disabled': {
              backgroundColor: '#E5E5E5',
              color: '#BDBDBD',
            },
          }}
        >
          {isRevoking ? 'Revoking...' : 'Revoke All Access'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevokeAccessDialog;
